import type { ObjectCategory } from '@gmn-portal/backend';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Eye, Folder, MoreHorizontal, Plus } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useDeleteObjectCategory, useObjectCategories } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  SortableHeader,
  TableCard,
  TableCardContent,
  TableCardFooter,
  TableCardHeader,
  TableLoadingCell,
  TablePagination,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyTableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { getSortParam } from '@/shared/utils/table-utils';

const ActionCell = ({
  category,
  onDelete,
  onViewDetail,
  isGmnAdmin,
}: {
  category: ObjectCategory;
  onDelete: (category: ObjectCategory) => void;
  onViewDetail: (id: string) => void;
  isGmnAdmin: boolean;
}) => {
  const { t } = useIntl();
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onViewDetail(category.id);
        }}
        className="p-1.5 hover:bg-secondary text-primary rounded-md transition-colors"
        aria-label={t('common.view')}
        title={t('common.view')}
      >
        <Eye size={16} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(event) => event.stopPropagation()}
            className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
            aria-label={t('navigation.openMenu')}
            title={t('navigation.openMenu')}
          >
            <MoreHorizontal size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => navigate(`/gmn/settings/object-categories/${category.id}`)}
          >
            {t('common.edit')}
          </DropdownMenuItem>
          {isGmnAdmin && (
            <DropdownMenuItem
              onClick={() => {
                window.setTimeout(() => onDelete(category), 0);
              }}
            >
              {t('common.delete')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export function ObjectCategoriesOverview() {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isGmnAdmin = currentUser?.role === 'gmn-admin';

  const tablePreferences = useTablePreferences({
    tableId: 'object-categories-overview',
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [],
      columnFilters: [],
      columnVisibility: {},
      searchTerm: '',
    },
  });

  const preferences = tablePreferences.loadPreferences();

  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );

  const [categoryToDelete, setCategoryToDelete] = useState<ObjectCategory | null>(null);
  const deleteCategory = useDeleteObjectCategory();

  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      searchTerm,
    });
  }, [pagination, sorting, columnFilters, columnVisibility, searchTerm, tablePreferences]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleViewDetail = useCallback(
    (id: string) => {
      navigate(`/gmn/settings/object-categories/${id}`);
    },
    [navigate]
  );

  const handleDelete = async () => {
    if (categoryToDelete) {
      try {
        await deleteCategory.mutateAsync(categoryToDelete.id);
        toast.success(t('objectCategories.categoryDeletedSuccessfully'));
        setCategoryToDelete(null);
      } catch (_error) {
        toast.error(t('objectCategories.errorDeletingCategory'));
      }
    }
  };

  const columns: ColumnDef<ObjectCategory>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} title={t('common.name')} />,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Folder size={16} className="text-primary" />
          <span className="font-medium text-foreground">{row.getValue('name')}</span>
        </div>
      ),
      size: 250,
      minSize: 150,
      maxSize: 400,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => <SortableHeader column={column} title={t('common.description')} />,
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.getValue('description') || '-'}</span>
      ),
      size: 400,
      minSize: 200,
      maxSize: 600,
    },
    {
      id: 'metadataFields',
      header: t('objectCategories.metadataFields'),
      cell: ({ row }) => {
        const schema = row.original.metadataSchema as {
          properties?: Record<string, unknown>;
        } | null;
        const fieldCount = schema?.properties ? Object.keys(schema.properties).length : 0;
        return <span className="text-foreground">{fieldCount}</span>;
      },
      size: 120,
      minSize: 80,
      maxSize: 150,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <SortableHeader column={column} title={t('common.createdAt')} />,
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string;
        return (
          <span className="text-muted-foreground">
            {date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}
          </span>
        );
      },
      size: 120,
      minSize: 100,
      maxSize: 150,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <ActionCell
          category={row.original}
          onDelete={setCategoryToDelete}
          onViewDetail={handleViewDetail}
          isGmnAdmin={isGmnAdmin}
        />
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  const { data: categoriesData, isLoading } = useObjectCategories({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    where: debouncedSearchTerm ? { name: { like: debouncedSearchTerm } } : undefined,
    sort: getSortParam<ObjectCategory>(sorting),
  });

  const categories = categoriesData?.docs || [];
  const totalPages = categoriesData?.totalPages || 0;
  const totalDocs = categoriesData?.totalDocs || 0;

  const table = useReactTable({
    data: categories,
    columns,
    pageCount: totalPages,
    rowCount: totalDocs,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  return (
    <PageContainer>
      <PageTitle
        icon={Folder}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{t('navigation.items.gmnSettings')}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>
              {t('navigation.items.gmnObjectCategories')}
            </SimpleBreadcrumbItem>
          </>
        }
        title={t('objectCategories.objectCategories')}
        primaryAction={{
          label: t('objectCategories.addCategory'),
          onClick: () => navigate('/gmn/settings/object-categories/new'),
          icon: <Plus size={16} />,
          disabled: !isGmnAdmin,
        }}
      />

      <TableCard>
        <TableCardHeader>
          <TableSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder={t('objectCategories.searchPlaceholder')}
          />
        </TableCardHeader>

        <TableCardContent>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeaderRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <TableHead
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                      className={
                        index === 0
                          ? 'pl-6'
                          : index === headerGroup.headers.length - 1
                            ? 'pr-6 text-right'
                            : ''
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableHeaderRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className="cursor-pointer"
                    onClick={() => handleViewDetail(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}px` }}
                        className={
                          index === 0
                            ? 'pl-6'
                            : index === row.getVisibleCells().length - 1
                              ? 'pr-6'
                              : ''
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !isLoading ? (
                <EmptyTableRow colSpan={columns.length} />
              ) : (
                <TableLoadingCell colSpan={columns.length} />
              )}
            </TableBody>
          </Table>
        </TableCardContent>

        <TableCardFooter>
          <TablePagination table={table} />
        </TableCardFooter>
      </TableCard>

      <AlertDialog
        open={!!categoryToDelete}
        onOpenChange={(open) => !open && setCategoryToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('objectCategories.deleteCategory')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('objectCategories.deleteCategoryConfirmation', {
                name: categoryToDelete?.name || t('objectCategories.category'),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteCategory.isPending}>
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
